import { api, loginUrl, onSessionEnd } from './api'

/** How often the clock is compared with the session's end: the wall clock stays right across sleep and throttled timers. */
const TICK_MS = 15_000
/** A look that failed (offline, server down) is tried again a minute later. */
const RETRY_MS = 60_000
/** Past the end the API names, the session has ended for certain. */
const GRACE_MS = 1_000

const CHANNEL = 'pedfx.session'

let leaving: 'ended' | 'logout' | undefined
let channel: BroadcastChannel | undefined

/** The session ended while the page was open: nothing more is saved here, and unsaved annotations stay as drafts (AUTH-08). */
export const sessionEnded = () => leaving === 'ended'

/** The login asks for the password even while Keycloak's own session lasts: an open, unattended page would otherwise log itself in again. */
function toLogin(): void {
  if (leaving) return
  leaving = 'ended'
  channel?.postMessage('ended')
  window.location.assign(loginUrl(undefined, 'login'))
}

/**
 * Leads to the login page as soon as the session ends while the app is open (AUTH-08): when a request
 * is refused for it, once the end the API names has passed (asking extends nothing), when the tab
 * shows again after it, and when another tab saw it end or logged out. Returns the stop.
 */
export function watchSession(): () => void {
  let due = 0
  const check = async () => {
    due = Date.now() + RETRY_MS
    try {
      const { seconds } = await api<{ seconds: number }>('/auth/session', { background: true })
      due = Date.now() + seconds * 1000 + GRACE_MS
    } catch {
      // A refusal leads to the login already; anything else is looked at again later.
    }
  }
  const tick = () => {
    if (Date.now() >= due) void check()
  }
  const shown = () => {
    if (document.visibilityState === 'visible') tick()
  }
  // Each tab looks for itself, so one that logged in again meanwhile stays.
  const told = () => void check()

  const stopListening = onSessionEnd(toLogin)
  const timer = window.setInterval(tick, TICK_MS)
  document.addEventListener('visibilitychange', shown)
  channel = typeof BroadcastChannel === 'undefined' ? undefined : new BroadcastChannel(CHANNEL)
  channel?.addEventListener('message', told)
  return () => {
    stopListening()
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', shown)
    channel?.close()
    channel = undefined
  }
}

/** Logs out on the platform and at Keycloak, whose page then leads to the login; the other tabs follow (AUTH-05). */
export async function logOut(): Promise<void> {
  leaving = 'logout' // a request refused meanwhile must not lead to the login in place of Keycloak's logout
  try {
    const { logout_url } = await api<{ logout_url: string }>('/auth/logout', { method: 'POST' })
    channel?.postMessage('ended')
    window.location.assign(logout_url)
  } catch (error) {
    leaving = undefined
    throw error
  }
}
